import { useDidMount } from 'hooks/useDidMount';
import React, { useEffect, useState } from 'react';
import {
  changeSelectedWorkspaceId,
  isFromChromeExtension,
  OnClosePostEditorAction
} from '../../../providers/AppProvider';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GetMeDocument, GetMeQuery, PostSource } from '../../../models/graphql';
import { upperCase } from 'lodash';
import { NewPostParamsType, useOpenPostEditor } from '../../../hooks/useOpenPostEditor';
import { getNewAccessToken, useAuth } from '../../../hooks/useAuth';
import { Loader } from '../../../components/containers/Loader';
import { ApolloQueryResult } from '@apollo/client/core';
import { useApolloClient } from '@apollo/client';
import { chromeExtensionHelpers } from '../../../helpers/chromeExtensionHelpers';
import { ErrorMessageSeverity } from '../../../constants/form';
import { useUserData } from '../../../hooks/useUserData';
import { Modal } from '../../../components/containers/Modal';

// This is ok as a file variable as it's for the Chrome Extension.
let hasSelectedWorkspace = false;

const isPostSourceGuard = (value: string): value is PostSource => {
  for (const enumValue of Object.values(PostSource)) {
    if (enumValue === value) {
      return true;
    }
  }
  return false;
};
export const PostEditorPageView = (): JSX.Element => {
  const apolloClient = useApolloClient();
  const { userData } = useUserData();
  const { isLoggedIn } = useAuth();
  const { openNewPost } = useOpenPostEditor();
  const [searchParams] = useSearchParams();
  const [closed, setClosed] = useState(false);
  const navigate = useNavigate();
  const srcParam = searchParams.get('src') || undefined;
  const urlParam = searchParams.get('url') || undefined;
  const imageUrlParam = searchParams.get('img_url') || undefined;
  const textParam = searchParams.get('text') || undefined;
  //const versionParam = searchParams.get('version') || undefined; // TODO event tracking to match legacy?
  const totalWorkspaces =
    userData?.organizations.reduce((total, org) => {
      return total + org.workspaces.length;
    }, 0) || 0;

  // If we're not from the chrome extension, the workspace either gets picked automatically or the user selected it.
  if (totalWorkspaces === 1 || !isFromChromeExtension) {
    hasSelectedWorkspace = true;
  }

  // Calling navigate() directly in the onClose callback wouldn't work for some reason.
  useEffect(() => {
    if (closed && !isFromChromeExtension) {
      navigate({ pathname: '/' });
    }
  }, [closed, navigate]);

  const onClose: OnClosePostEditorAction = success => {
    setClosed(true);
    if (isFromChromeExtension && window.parent) {
      chromeExtensionHelpers.sendPostEditorClosedMessage(success);
    }
  };

  // Because of local storage partitioning, every website the user clicks the chrome extension on will need to
  // request a new access token.
  // TODO How this will work with multiple workspaces/orgs? Need a switcher built into the post editor? Built into the extension?
  useEffect(() => {
    if (!isLoggedIn && isFromChromeExtension) {
      getNewAccessToken()
        .then(() => {
          apolloClient
            .query({
              query: GetMeDocument,
              fetchPolicy: 'network-only'
            })
            .then((data: ApolloQueryResult<GetMeQuery>) => {
              if (data.data?.me?.organizations[0].workspaces[0].id) {
                changeSelectedWorkspaceId(data.data.me.organizations[0].workspaces[0].id);
              } else {
                chromeExtensionHelpers.sendErrorMessage();
              }
            })
            .catch(() => {
              chromeExtensionHelpers.sendErrorMessage();
            });
        })
        .catch(() => {
          chromeExtensionHelpers.sendErrorMessage();
        });
    }
  }, [apolloClient, isLoggedIn, searchParams]);

  useDidMount(() => {
    // If we're opening the page but not logged in yet.
    // This is used by the chrome extension to stay on this endpoint while dealing with auth flow.
    if (!isLoggedIn || !hasSelectedWorkspace) {
      return;
    }

    let postSource = PostSource.POST_EDITOR;
    if (srcParam) {
      const upper = upperCase(srcParam);
      if (isPostSourceGuard(upper)) {
        postSource = PostSource[upper];
      }
    }

    // Always trigger the profile required warning off the get-go for chrome extension.
    let initialValidation: NewPostParamsType['initialValidation'] = undefined;
    if (isFromChromeExtension) {
      initialValidation = {
        severity: ErrorMessageSeverity.warning,
        keys: ['profileRequired']
      };
    }

    openNewPost({
      source: postSource,
      groupContent: {
        texts: textParam ? [textParam] : undefined,
        link: urlParam,
        uploadImmediatelyImageUrls: imageUrlParam ? [imageUrlParam] : undefined
      },
      initialValidation,
      onClose
    });
  });

  if (!isLoggedIn) {
    return <Loader variation={'dark'} size={'large'} type={'overlay-transparent'} />;
  }

  // If we're from the chrome extension and have access to more than 1 workspace, and we haven't selected one yet,
  // then we need to show a workspace "switcher" modal that selects the workspace. When it selects the workspace, it
  // sets a file variable that doesn't change on unmount, and the whole app gets re-mounted by the
  // changeSelectedWorkspaceId function, which opens the post editor on the useDidMount logic above.
  if (isFromChromeExtension && totalWorkspaces > 1 && !hasSelectedWorkspace) {
    return (
      <Modal
        showOverlay={false}
        title={'Select a Workspace'}
        onClose={() => chromeExtensionHelpers.sendPostEditorClosedMessage(false)}
        closeOnClickOutside={false}>
        {userData?.organizations.map(org => (
          <li key={org.id} style={{ listStyleType: 'none' }}>
            <h3>{org.name}</h3>
            {org.workspaces.map(workspace => (
              <ul key={workspace.id} style={{ marginLeft: 0, marginBottom: 0 }}>
                <a
                  onClick={() => {
                    hasSelectedWorkspace = true;
                    changeSelectedWorkspaceId(workspace.id);
                  }}>
                  {workspace.name}
                </a>
              </ul>
            ))}
            <hr />
          </li>
        ))}
      </Modal>
    );
  }

  return <></>;
};
